"use strict";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result) __defProp(target, key, result);
  return result;
};
import { LitElement, html, nothing } from "lit";
import { keyed } from "lit/directives/keyed.js";
import { state, property } from "lit/decorators.js";
import { STCComponentMixin } from "../../../common/mixins/stc_component_mixin";
import "./microsite_html_import_dialog";
import "./microsite_page_manager";
import "./microsite_html_builder";
export class MicrositePageBuilder extends STCComponentMixin(LitElement) {
  constructor() {
    super(...arguments);
    this.previewMode = false;
    this.deviceView = "desktop";
    this.pages = [];
    this.currentPageId = null;
    this.loading = true;
    this.canManageTemplates = false;
    this.siteVersioningEnabled = false;
    this.siteVersionsUrl = "";
    this.micrositeId = null;
    this.historyMode = false;
    this.historyVersions = [];
    this.historyLoading = false;
    this.historyError = "";
    this.selectedHistoryVersionId = null;
    this.restoringVersionId = null;
    this.previewingVersionId = null;
    this.historyPages = [];
    this.hasPersonalizationOptions = false;
    this.htmlEditorHasContent = false;
    this.editorMode = "visual";
    this.handlePagesSync = (e) => {
      const detail = e.detail;
      if (detail?.pages) {
        this.pages = detail.pages;
      }
      if (detail?.currentPageId !== void 0) {
        this.currentPageId = detail.currentPageId;
      }
      if (detail?.htmlEditorHasContent !== void 0) {
        this.htmlEditorHasContent = Boolean(detail.htmlEditorHasContent);
      }
    };
    this.handlePageSelect = (e) => {
      e.stopPropagation();
      const detail = e.detail;
      if (detail?.pageId !== void 0) {
        this.currentPageId = detail.pageId;
        if (this.historyMode) {
          this.publishHistoryPage(detail.pageId);
        }
        document.dispatchEvent(
          new CustomEvent("microsite:page-select", {
            bubbles: true,
            composed: true,
            detail: {
              pageId: detail.pageId,
              titleForNewPage: detail.titleForNewPage,
              initialPuckData: detail.initialPuckData
            }
          })
        );
      }
    };
    this.handlePagesUpdate = (e) => {
      e.stopPropagation();
      const detail = e.detail;
      if (detail?.pages) {
        this.pages = detail.pages;
        document.dispatchEvent(
          new CustomEvent("microsite:pages-update", {
            bubbles: true,
            composed: true,
            detail: { pages: detail.pages }
          })
        );
      }
    };
    this.openVersionHistory = () => {
      this.historyMode = true;
      void this.loadHistoryVersions();
    };
    this.handleHistoryPageChange = (event) => {
      const detail = event.detail;
      const value = detail?.value ?? event.target.value;
      const pageId = parseInt(value ?? "", 10);
      if (Number.isNaN(pageId) || pageId === this.currentPageId) return;
      this.currentPageId = pageId;
      this.publishHistoryPage(pageId);
    };
  }
  connectedCallback() {
    super.connectedCallback();
    this.initializeFromBootData();
    this.loading = false;
    document.addEventListener("microsite:pages-sync", this.handlePagesSync);
    this.addEventListener("microsite:view-history", this.openVersionHistory);
    this.scheduleMicrositeReactInit();
  }
  /** Re-run micro app init after connect (hi-ready can promote the template after tryInit’s first retries). */
  scheduleMicrositeReactInit() {
    const run = (attempt) => {
      if (typeof window.__eabInitMicrosite === "function") {
        window.__eabInitMicrosite();
        return;
      }
      if (attempt < 50) {
        window.setTimeout(() => run(attempt + 1), 100);
      }
    };
    queueMicrotask(() => run(0));
  }
  initializeFromBootData() {
    const scriptEl = document.getElementById("eab-microsite-boot-data");
    if (scriptEl?.textContent) {
      try {
        const parsed = JSON.parse(scriptEl.textContent);
        this.canManageTemplates = Boolean(parsed.can_manage_templates);
        this.siteVersioningEnabled = parsed.site_versioning_enabled === true;
        this.siteVersionsUrl = parsed.site_versions_url || "";
        this.micrositeId = parsed.microsite_id ?? null;
        this.editorMode = parsed.is_html === true ? "html" : "visual";
        if (Array.isArray(parsed.microsite_pages) && parsed.microsite_pages.length > 0) {
          this.pages = parsed.microsite_pages.map((p, idx) => ({
            id: p.id,
            title: p.title ?? "Untitled",
            name: p.title ?? null,
            slug: p.slug ?? null,
            is_home_page: Boolean(p.is_home_page),
            order: p.order ?? idx + 1,
            created_at: (/* @__PURE__ */ new Date()).toISOString(),
            updated_at: (/* @__PURE__ */ new Date()).toISOString()
          }));
          const bootPageId = parsed.microsite_page_id !== void 0 && parsed.microsite_page_id !== null ? Number(parsed.microsite_page_id) : null;
          if (bootPageId !== null && this.pages.some((p) => p.id === bootPageId)) {
            this.currentPageId = bootPageId;
          } else {
            const home = this.pages.find((p) => p.is_home_page) ?? this.pages[0];
            if (home) this.currentPageId = home.id;
          }
        }
      } catch {
      }
    }
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("microsite:pages-sync", this.handlePagesSync);
    this.removeEventListener("microsite:view-history", this.openVersionHistory);
  }
  updated(changedProperties) {
    if (changedProperties.has("previewMode") && !this.previewMode) {
      this.historyMode = false;
      this.historyPages = [];
    }
  }
  handleEdit() {
    document.dispatchEvent(new CustomEvent("microsite-editor:edit", { bubbles: true, composed: true }));
  }
  setDeviceView(view) {
    this.deviceView = view;
  }
  async loadHistoryVersions() {
    if (!this.siteVersionsUrl) {
      this.historyError = "Version history is not available.";
      return;
    }
    this.historyLoading = true;
    this.historyError = "";
    try {
      const response = await fetch(this.siteVersionsUrl, { headers: { Accept: "application/json" } });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !Array.isArray(data.versions)) {
        throw new Error("Versions could not be loaded.");
      }
      this.historyVersions = data.versions;
      const selected = data.versions.find((version) => version.current) ?? data.versions[0];
      if (selected) {
        await this.selectHistoryVersion(selected.id);
      }
    } catch (error) {
      this.historyError = error instanceof Error ? error.message : "Versions could not be loaded.";
    } finally {
      this.historyLoading = false;
    }
  }
  async selectHistoryVersion(versionId, showLoader = false) {
    if (this.previewingVersionId !== null) return;
    this.selectedHistoryVersionId = versionId;
    this.historyError = "";
    if (showLoader) this.previewingVersionId = versionId;
    try {
      const response = await fetch(`${this.siteVersionsUrl}/${versionId}`, { headers: { Accept: "application/json" } });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !Array.isArray(data.pages)) {
        this.historyError = "This version could not be opened.";
        return;
      }
      this.historyPages = data.pages;
      const selected = this.historyPages.find((page) => page.id === this.currentPageId) ?? this.historyPages.find((page) => page.is_home_page) ?? this.historyPages[0];
      this.currentPageId = selected?.id ?? null;
      this.publishHistoryPage(this.currentPageId);
    } catch {
      this.historyError = "This version could not be opened.";
    } finally {
      this.previewingVersionId = null;
    }
  }
  publishHistoryPage(pageId) {
    const page = this.historyPages.find((item) => item.id === pageId) ?? this.historyPages[0];
    this.dispatchEvent(
      new CustomEvent("microsite:history-preview", {
        bubbles: true,
        composed: true,
        detail: {
          content: page?.content ?? { root: { props: {} }, content: [], zones: {} },
          pages: this.historyPages
        }
      })
    );
  }
  async restoreHistoryVersion(version) {
    if (this.restoringVersionId !== null) return;
    const confirmed = window.confirm(
      `Restore version ${version.version_number}? This creates a new version from it.`
    );
    if (!confirmed) return;
    this.restoringVersionId = version.id;
    this.historyError = "";
    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content ?? "";
      const response = await fetch(this.siteVersionsUrl, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": csrf
        },
        body: JSON.stringify({ restored_from_site_version_id: version.id })
      });
      if (!response.ok) {
        throw new Error("This version could not be restored.");
      }
      this.clearLocalDrafts();
      window.location.reload();
    } catch (error) {
      this.historyError = error instanceof Error ? error.message : "This version could not be restored.";
      this.restoringVersionId = null;
    }
  }
  clearLocalDrafts() {
    if (this.micrositeId == null) return;
    [
      "eab_microsite_draft_",
      "eab_microsite_pages_draft_",
      "eab_microsite_content_",
      "eab_microsite_html_draft_v1_"
    ].forEach((prefix) => localStorage.removeItem(`${prefix}${this.micrositeId}`));
  }
  renderHistoryVersions() {
    if (this.historyLoading && this.historyVersions.length === 0) {
      return html`<hi-indicator></hi-indicator>`;
    }
    if (this.historyError && this.historyVersions.length === 0) {
      return html`<p>${this.historyError}</p>`;
    }
    if (this.historyVersions.length === 0) {
      return html`<p>No versions yet. Save the microsite to create the first version.</p>`;
    }
    return html`
      ${this.historyError ? html`<p>${this.historyError}</p>` : nothing}
      ${this.historyVersions.map(
      (version) => html`
          <div class="microsite-preview__version ${this.selectedHistoryVersionId === version.id ? "is-selected" : ""}">
            <div class="microsite-preview__version-meta">
              <span>Version ${version.version_number}</span>
              ${version.current ? html`<hi-badge theme="info">Current</hi-badge>` : nothing}
              ${version.published ? html`<hi-badge theme="success">Published</hi-badge>` : nothing}
              <span>${version.created_at_label}</span>
              ${version.created_by ? html`<span>${version.created_by}</span>` : nothing}
            </div>
            <div class="microsite-preview__version-actions">
              <hi-button
                size="small"
                aesthetic="secondary"
                ?disabled=${this.previewingVersionId !== null || this.restoringVersionId !== null}
                @trigger-ended=${() => this.selectHistoryVersion(version.id, true)}
              >
                ${this.previewingVersionId === version.id ? html`<hi-indicator></hi-indicator>` : html`Preview`}
              </hi-button>
              <hi-button
                size="small"
                ?disabled=${this.restoringVersionId !== null}
                @trigger-ended=${() => this.restoreHistoryVersion(version)}
              >
                Restore
              </hi-button>
            </div>
          </div>
        `
    )}
    `;
  }
  renderPreviewContainer() {
    return html`
      <div id="eab-microsite-preview-container" class="microsite-preview ${this.previewMode ? "microsite-preview--visible" : "microsite-preview--hidden"} ${this.historyMode ? "microsite-preview--history" : ""}">
        <div class="microsite-preview__wrapper">
          <div class="microsite-preview__header">
            <hi-button id="eab-microsite-edit-btn" aesthetic="secondary" @trigger-ended=${this.handleEdit}>
              <hi-icon glyph="arrow_back" mode="presentation"></hi-icon>
              <span>Back to Editor</span>
            </hi-button>
            <div class="microsite-preview__header-controls">
              ${this.historyMode && this.historyPages.length > 0 ? keyed(
      `${this.selectedHistoryVersionId}-${this.historyPages.map((page) => `${page.id}:${page.title}`).join(",")}`,
      html`
                      <select
                        class="microsite-preview__page-select"
                        .value=${this.currentPageId != null ? String(this.currentPageId) : ""}
                        @change=${this.handleHistoryPageChange}
                      >
                        ${this.historyPages.map(
        (page) => html`<option value=${String(page.id)}>${page.title || "Untitled"}</option>`
      )}
                      </select>
                    `
    ) : nothing}
              <hi-button-group id="eab-microsite-device-select">
                <hi-button
                  id="eab-microsite-preview-mobile"
                  title="Mobile"
                  ?selected=${this.deviceView === "mobile"}
                  @trigger-ended=${() => this.setDeviceView("mobile")}
                >
                  <hi-icon kind="mobile" mode="presentation"></hi-icon>
                  <span>Mobile</span>
                </hi-button>
                <hi-button
                  id="eab-microsite-preview-tablet"
                  title="Tablet"
                  ?selected=${this.deviceView === "tablet"}
                  @trigger-ended=${() => this.setDeviceView("tablet")}
                >
                  <hi-icon kind="tablet" mode="presentation"></hi-icon>
                  <span>Tablet</span>
                </hi-button>
                <hi-button
                  id="eab-microsite-preview-desktop"
                  title="Desktop"
                  ?selected=${this.deviceView === "desktop"}
                  @trigger-ended=${() => this.setDeviceView("desktop")}
                >
                  <hi-icon kind="laptop" mode="presentation"></hi-icon>
                  <span>Desktop</span>
                </hi-button>
              </hi-button-group>
            </div>
          </div>
          <div class="microsite-preview__body">
            ${this.historyMode ? html`<aside class="microsite-preview__versions">${this.renderHistoryVersions()}</aside>` : nothing}
            <div id="eab-microsite-preview-content" class="microsite-preview-content" style="position: relative;">
              ${this.previewingVersionId !== null ? html`
                    <div
                      style="position: absolute; inset: 0; z-index: 2; display: flex; align-items: center; justify-content: center; background: rgba(245, 245, 245, 0.72);"
                    >
                      <hi-indicator></hi-indicator>
                    </div>
                  ` : nothing}
              <div
                id="eab-microsite-preview-frame"
                class="microsite-preview-frame microsite-preview-frame--${this.deviceView} content"
              >
                <!-- Preview content will be rendered here -->
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }
  render() {
    return html`
      <!-- Page Manager -->
      <stc-microsite-page-manager
        editor-mode=${this.editorMode}
        .pages=${this.pages}
        .currentPageId=${this.currentPageId}
        .loading=${this.loading}
        .canManageTemplates=${this.canManageTemplates}
        .siteVersioningEnabled=${this.siteVersioningEnabled}
        .hasPersonalizationOptions=${this.hasPersonalizationOptions}
        ?html-editor-has-content=${this.htmlEditorHasContent}
        ?preview-mode=${this.previewMode}
        ?history-mode=${this.historyMode}
        @microsite:page-select=${this.handlePageSelect}
        @microsite:pages-update=${this.handlePagesUpdate}
      ></stc-microsite-page-manager>

      <!-- Empty: Lit must not own children here or React unmounts on re-render. -->
      <div id="eab-microsite-root" class=${this.previewMode ? "microsite-editor--hidden" : ""}></div>

      <!-- Preview Container -->
      ${this.renderPreviewContainer()}
    `;
  }
}
__decorateClass([
  property({ type: Boolean, attribute: "data-preview-mode", reflect: true })
], MicrositePageBuilder.prototype, "previewMode", 2);
__decorateClass([
  state()
], MicrositePageBuilder.prototype, "deviceView", 2);
__decorateClass([
  state()
], MicrositePageBuilder.prototype, "pages", 2);
__decorateClass([
  state()
], MicrositePageBuilder.prototype, "currentPageId", 2);
__decorateClass([
  state()
], MicrositePageBuilder.prototype, "loading", 2);
__decorateClass([
  state()
], MicrositePageBuilder.prototype, "canManageTemplates", 2);
__decorateClass([
  state()
], MicrositePageBuilder.prototype, "siteVersioningEnabled", 2);
__decorateClass([
  state()
], MicrositePageBuilder.prototype, "siteVersionsUrl", 2);
__decorateClass([
  state()
], MicrositePageBuilder.prototype, "micrositeId", 2);
__decorateClass([
  state()
], MicrositePageBuilder.prototype, "historyMode", 2);
__decorateClass([
  state()
], MicrositePageBuilder.prototype, "historyVersions", 2);
__decorateClass([
  state()
], MicrositePageBuilder.prototype, "historyLoading", 2);
__decorateClass([
  state()
], MicrositePageBuilder.prototype, "historyError", 2);
__decorateClass([
  state()
], MicrositePageBuilder.prototype, "selectedHistoryVersionId", 2);
__decorateClass([
  state()
], MicrositePageBuilder.prototype, "restoringVersionId", 2);
__decorateClass([
  state()
], MicrositePageBuilder.prototype, "previewingVersionId", 2);
__decorateClass([
  state()
], MicrositePageBuilder.prototype, "historyPages", 2);
__decorateClass([
  property({ type: Boolean, attribute: "data-has-personalization-options", reflect: true })
], MicrositePageBuilder.prototype, "hasPersonalizationOptions", 2);
__decorateClass([
  state()
], MicrositePageBuilder.prototype, "htmlEditorHasContent", 2);
__decorateClass([
  property({ type: String, attribute: "editor-mode", reflect: true })
], MicrositePageBuilder.prototype, "editorMode", 2);
customElements.define("stc-microsite-page-builder", MicrositePageBuilder);
